import type { ReadAloudMode } from "../app/read-aloud.js";
import { el } from "../lib/dom.js";
import { icon } from "../lib/icon.js";
import { frame } from "./sheet.js";

/**
 * DCTA-162 F29 + F33 — the Read aloud sheet behind Me's row (spoken.md §1.2, every word from there). The three choices are
 * radios in the design's radio rows (the sign-in's `.persona`: title, line, the check circle), one tab stop, arrow keys
 * moving the choice as radios do; the line under them says what is never read aloud; the help line tells a
 * screen-reader user to choose Never (a screen reader already reads each message, F33). A choice is the customer's own
 * setting: kept at once (the caller writes it to the bank), Done closes. The bank did not keep it: the choice goes back,
 * one line says so (Me's own words for it).
 */
const CHOICES: readonly { readonly mode: ReadAloudMode; readonly title: string; readonly line: string }[] = [
  { mode: "when_i_speak", title: "When I speak", line: "When you ask out loud, the answer is read aloud. Typed requests stay silent." },
  { mode: "always", title: "Always", line: "Every question, reply and card is read aloud." },
  { mode: "never", title: "Never", line: "Nothing is read aloud." },
];
const UNDER = "Balances, card numbers and codes are never read aloud. They stay on the screen.";
const SCREEN_READER = "Using a screen reader? Choose Never. It already reads each message.";
const NOT_KEPT = "Couldn't save that. Nothing has changed.";

/** Me's row value for each choice (§1.2). */
export const READ_ALOUD_VALUE: Readonly<Record<ReadAloudMode, string>> = { when_i_speak: "When I speak", always: "Always", never: "Never" };

export interface ReadAloudSheetHandlers {
  readonly onChoose: (mode: ReadAloudMode) => void;
  readonly onDone: () => void;
}

export function readAloudSheet(mode: ReadAloudMode, h: ReadAloudSheetHandlers): { readonly sheet: HTMLElement; failed(back: ReadAloudMode): void } {
  let chosen = mode;
  const status = el("p", { class: "save-note", role: "status" });
  const radios = CHOICES.map((c) => {
    const radio = el("button", { class: "raw persona choice", type: "button", role: "radio", data: { mode: c.mode } }, [
      el("span", { class: "tx" }, [el("b", {}, [c.title]), el("small", {}, [c.line])]),
      el("span", { class: "ck", aria: { hidden: "true" } }, [icon("i-check")]),
    ]);
    radio.addEventListener("click", () => pick(c.mode));
    radio.addEventListener("keydown", (event) => {
      const step = event.key === "ArrowDown" || event.key === "ArrowRight" ? 1 : event.key === "ArrowUp" || event.key === "ArrowLeft" ? -1 : 0;
      if (step === 0) return;
      event.preventDefault();
      const at = CHOICES.findIndex((x) => x.mode === c.mode);
      const next = CHOICES[(at + step + CHOICES.length) % CHOICES.length];
      if (next === undefined) return;
      pick(next.mode);
      radios[CHOICES.indexOf(next)]?.focus();
    });
    return radio;
  });
  const paint = (): void => {
    radios.forEach((radio, i) => {
      const on = CHOICES[i]?.mode === chosen;
      radio.setAttribute("aria-checked", on ? "true" : "false");
      radio.setAttribute("tabindex", on ? "0" : "-1");
    });
  };
  const pick = (next: ReadAloudMode): void => {
    if (next === chosen) return;
    chosen = next;
    status.textContent = "";
    paint();
    h.onChoose(next);
  };
  const group = el("div", { class: "list choices", role: "radiogroup" }, radios);
  const done = el("button", { class: "raw btn", type: "button" }, ["Done"]);
  done.addEventListener("click", () => h.onDone());
  const sheet = frame("Read aloud", el("div", { class: "key", aria: { hidden: "true" } }, [icon("i-speaker")]), [
    group,
    status,
    el("p", { class: "fine" }, [UNDER]),
    el("p", { class: "fine" }, [SCREEN_READER]),
    el("div", { class: "acts" }, [done]),
  ]);
  sheet.classList.add("short", "read-aloud");
  const title = sheet.querySelector("h2");
  if (title !== null) group.setAttribute("aria-labelledby", title.id);
  paint();
  return {
    sheet,
    failed(back) {
      chosen = back;
      paint();
      status.textContent = NOT_KEPT;
    },
  };
}
